/**
 * Date / time formatting helpers for the Freisland APCC frontend.
 *
 * All times are displayed in the CET (Europe/Amsterdam) timezone so that
 * the AP team in the Netherlands sees consistent, local timestamps
 * regardless of where the browser is running.
 *
 * IMPORTANT: The backend (Python/SQLAlchemy) stores all timestamps in UTC
 * but may omit the "Z" or "+00:00" suffix. Without a timezone indicator
 * JavaScript's `new Date()` treats the string as *local* browser time,
 * which silently shifts every timestamp by the browser's UTC offset.
 * `toUTC()` below normalises the input so the conversion to Amsterdam
 * time is always correct.
 */

const TZ = 'Europe/Amsterdam'

/**
 * Ensure a date string is interpreted as UTC.
 *
 * If the string already contains timezone info (Z, +, or - offset after
 * the time portion) it is returned as-is.  Otherwise we append "Z" so
 * that `new Date()` treats it as UTC rather than the browser's local tz.
 */
function toUTC(dateStr: string): string {
  // Already has timezone info  (ends with Z, or has +/-HH:MM after time)
  if (/Z$/i.test(dateStr) || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
    return dateStr
  }
  // Append Z to treat as UTC
  return dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
}

/** Short date — "May 11, 2026" */
export function formatDate(dateStr: string): string {
  return new Date(toUTC(dateStr)).toLocaleDateString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Compact date — "11 May 2025" (en-GB style) */
export function formatDateCompact(dateStr: string): string {
  return new Date(toUTC(dateStr)).toLocaleDateString('en-GB', {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** Date + time — "May 11, 2026, 2:35 PM" */
export function formatDateTime(dateStr: string): string {
  return new Date(toUTC(dateStr)).toLocaleString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/** Time only — "2:35 PM" */
export function formatTime(dateStr: string): string {
  return new Date(toUTC(dateStr)).toLocaleTimeString('en-US', {
    timeZone: TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/** Full timestamp — "May 11, 2026, 2:35:09 PM CET" */
export function formatTimestamp(dateStr: string): string {
  return new Date(toUTC(dateStr)).toLocaleString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  })
}
